import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Music, ChevronDown, ChevronUp, Play, Pause,
  SkipForward, SkipBack, Clock, GripVertical, X, Edit2, List, Car,
  Square
} from 'lucide-react';

export default function SetlistCard({
  setlist,
  isExpanded,
  onToggleExpand,
  // Playback
  isCurrentlyPlaying,
  isPlaying,
  currentItemIndex,
  currentTime,
  currentDuration,
  onStartPlayback,
  onJumpToSong,
  onTogglePlayPause,
  onSkipToNext,
  onSkipToPrevious,
  onSkipBackward,
  onSkipForward,
  onSeek,
  onStopPlayback,
  onToggleContinuousMode,
  // Actions
  onDelete,
  onEdit,
  onAddSong,
  onRemoveItem,
  onUpdateGapTime,
  onReorderItems,
  // Helpers
  getSongById,
  formatDuration,
  calculateTotalDuration,
  isMobile
}) {
  const continuousMode = !!setlist.continuousMode;
  const totalDuration = calculateTotalDuration(setlist.items, continuousMode);

  // ── Seek bar (clickable + draggable) ──────────────────────
  const seekBarRef = useRef(null);
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [dragSeekTime, setDragSeekTime] = useState(null);

  const calcSeekTime = useCallback((clientX) => {
    if (!seekBarRef.current || !currentDuration) return 0;
    const rect = seekBarRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    return ratio * currentDuration;
  }, [currentDuration]);

  const handleSeekStart = (e) => {
    e.preventDefault();
    setIsDraggingSeek(true);
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    setDragSeekTime(calcSeekTime(clientX));
  };

  useEffect(() => {
    if (!isDraggingSeek) return;

    const onMove = (e) => {
      const clientX = e.clientX ?? e.touches?.[0]?.clientX;
      if (clientX == null) return;
      setDragSeekTime(calcSeekTime(clientX));
    };

    const onEnd = () => {
      if (dragSeekTime != null) onSeek(dragSeekTime);
      setIsDraggingSeek(false);
      setDragSeekTime(null);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onEnd);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDraggingSeek, dragSeekTime, calcSeekTime, onSeek]);

  const displayedTime = isDraggingSeek && dragSeekTime != null ? dragSeekTime : currentTime;
  const progressPercent = currentDuration ? (displayedTime / currentDuration) * 100 : 0;

  // ── Drag and drop (setlist reordering) ────────────────────
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);
  const containerRef = useRef(null);
  const dragStateRef = useRef({ draggedIndex: null, insertIndex: null });
  const ghostRef = useRef(null);
  const draggedElementRef = useRef(null);

  const createGhost = (element, y) => {
    const rect = element.getBoundingClientRect();
    const ghost = element.cloneNode(true);
    ghost.style.position = 'fixed';
    ghost.style.width = `${rect.width}px`;
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${y - 30}px`;
    ghost.style.opacity = '0.9';
    ghost.style.pointerEvents = 'none';
    ghost.style.zIndex = '9999';
    ghost.style.transform = 'scale(1.02)';
    ghost.style.boxShadow = '0 10px 40px rgba(129, 70, 255, 0.4)';
    ghost.style.borderRadius = '16px';
    ghost.style.transition = 'none';
    ghost.id = 'drag-ghost-setlist';
    document.body.appendChild(ghost);
    return ghost;
  };

  const moveGhost = (y) => { if (ghostRef.current) ghostRef.current.style.top = `${y - 30}px`; };

  const removeGhost = () => {
    if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
  };

  const calculateInsertIdx = (y, fromIndex, containerEl) => {
    if (!containerEl) return null;
    const elements = containerEl.querySelectorAll('[data-item-index]');
    let newInsert = null;
    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const idx = parseInt(el.dataset.itemIndex, 10);
      const mid = rect.top + rect.height / 2;
      if (y < mid && y >= rect.top - 20) {
        if (idx !== fromIndex && idx !== fromIndex + 1) newInsert = idx;
      } else if (y >= mid && y <= rect.bottom + 20) {
        if (idx + 1 !== fromIndex && idx !== fromIndex) newInsert = idx + 1;
      }
    });
    return newInsert;
  };

  const performReorder = () => {
    const { draggedIndex: fromIndex, insertIndex: toInsert } = dragStateRef.current;
    if (fromIndex !== null && toInsert !== null) {
      let target = toInsert > fromIndex ? toInsert - 1 : toInsert;
      if (target !== fromIndex && target >= 0) {
        const reordered = [...setlist.items];
        const [moved] = reordered.splice(fromIndex, 1);
        reordered.splice(target, 0, moved);
        onReorderItems(reordered);
      }
    }
  };

  const resetDragState = () => {
    removeGhost();
    if (draggedElementRef.current) {
      draggedElementRef.current.style.opacity = '1';
      draggedElementRef.current.style.transform = '';
      draggedElementRef.current = null;
    }
    dragStateRef.current = { draggedIndex: null, insertIndex: null };
    setDraggedIndex(null);
    setInsertIndex(null);
  };

  const handleMouseDown = (e, index) => {
    if (isMobile) return;
    e.preventDefault();
    const element = e.currentTarget.closest('[data-item-index]');
    draggedElementRef.current = element;
    element.style.opacity = '0.4';
    element.style.transform = 'scale(0.98)';
    setDraggedIndex(index);
    dragStateRef.current.draggedIndex = index;
    ghostRef.current = createGhost(element, e.clientY);

    const handleMouseMove = (moveEvent) => {
      moveGhost(moveEvent.clientY);
      const newInsert = calculateInsertIdx(moveEvent.clientY, dragStateRef.current.draggedIndex, containerRef.current);
      if (newInsert !== dragStateRef.current.insertIndex) {
        dragStateRef.current.insertIndex = newInsert;
        setInsertIndex(newInsert);
      }
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      performReorder();
      resetDragState();
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    const element = e.currentTarget.closest('[data-item-index]');
    draggedElementRef.current = element;
    element.style.opacity = '0.4';
    element.style.transform = 'scale(0.98)';
    setDraggedIndex(index);
    dragStateRef.current.draggedIndex = index;
    ghostRef.current = createGhost(element, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (dragStateRef.current.draggedIndex === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    moveGhost(touch.clientY);
    const newInsert = calculateInsertIdx(touch.clientY, dragStateRef.current.draggedIndex, containerRef.current);
    if (newInsert !== dragStateRef.current.insertIndex) {
      dragStateRef.current.insertIndex = newInsert;
      setInsertIndex(newInsert);
    }
  };

  const handleTouchEnd = () => {
    performReorder();
    resetDragState();
  };

  useEffect(() => { return () => removeGhost(); }, []);

  return (
    <div className="card-3d border border-transparent rounded-card overflow-hidden">
      {/* Setlist Header */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <List className={`w-5 h-5 flex-shrink-0 ${isCurrentlyPlaying ? 'text-green-400' : 'text-brand'}`} />
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold text-lg truncate flex items-center gap-2">
              {setlist.name}
              {continuousMode && <Car className="w-4 h-4 text-brand flex-shrink-0" title="Continuous mode" />}
            </h3>
            <p className="text-sm text-text-subtle">
              {setlist.items.length} song{setlist.items.length !== 1 ? 's' : ''} • {formatDuration(totalDuration)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="bg-transparent hover:bg-brand transition p-2 rounded-full"
            title="Edit setlist"
          >
            <Edit2 className="w-4 h-4 text-text-subtle hover:text-white" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="bg-transparent hover:bg-red-600 transition p-2 rounded-full"
            title="Delete setlist"
          >
            <Trash2 className="w-4 h-4 text-text-subtle hover:text-white" />
          </button>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-text-subtle" /> : <ChevronDown className="w-5 h-5 text-text-subtle" />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">

          {/* Playback Controls */}
          {setlist.items.length > 0 && (
            <div className="border border-surface-border-light rounded-card-btn p-4 mb-4">
              {isCurrentlyPlaying ? (
                <div className="space-y-3">
                  {/* Now Playing */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-green-400 font-semibold flex items-center gap-2">
                        NOW PLAYING
                        {continuousMode && (
                          <span className="bg-brand/30 text-brand px-1.5 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
                            <Car className="w-2.5 h-2.5" />
                            CONTINUOUS
                          </span>
                        )}
                      </p>
                      <p className="font-semibold truncate">
                        {getSongById(setlist.items[currentItemIndex]?.songId)?.name || 'Unknown'}
                      </p>
                      <p className="text-xs text-text-subtle">
                        Song {currentItemIndex + 1} of {setlist.items.length}
                      </p>
                    </div>
                  </div>

                  {/* Seek bar (clickable + draggable) */}
                  <div className="space-y-1">
                    <div
                      ref={seekBarRef}
                      onMouseDown={handleSeekStart}
                      onTouchStart={handleSeekStart}
                      className="h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer touch-none relative group"
                    >
                      <div
                        className="h-full bg-green-400 transition-all duration-100 pointer-events-none"
                        style={{ width: `${progressPercent}%` }}
                      />
                      {/* Drag thumb (always visible when playing) */}
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow pointer-events-none transition-opacity"
                        style={{
                          left: `calc(${progressPercent}% - 6px)`,
                          opacity: isDraggingSeek ? 1 : 0.85,
                          transform: `translateY(-50%) scale(${isDraggingSeek ? 1.3 : 1})`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-text-subtle">
                      <span className="tabular-nums">{formatDuration(displayedTime)}</span>
                      <span className="tabular-nums">{formatDuration(currentDuration)}</span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    <button
                      onClick={onSkipToPrevious}
                      className="bg-white/10 hover:bg-white/20 transition p-2 rounded-full"
                      title="Previous song / restart"
                    >
                      <SkipBack className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onSkipBackward}
                      className="bg-white/10 hover:bg-white/20 transition px-3 py-2 rounded-full text-xs font-semibold"
                      title="Back 10 seconds"
                    >
                      −10s
                    </button>
                    <button
                      onClick={onTogglePlayPause}
                      className="bg-green-500 hover:bg-green-600 transition p-3 rounded-full"
                      title={isPlaying ? 'Pause' : 'Play'}
                    >
                      {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={onSkipForward}
                      className="bg-white/10 hover:bg-white/20 transition px-3 py-2 rounded-full text-xs font-semibold"
                      title="Forward 10 seconds"
                    >
                      +10s
                    </button>
                    <button
                      onClick={onSkipToNext}
                      disabled={currentItemIndex >= setlist.items.length - 1}
                      className="bg-white/10 hover:bg-white/20 disabled:opacity-disabled disabled:cursor-not-allowed transition p-2 rounded-full"
                      title="Next song"
                    >
                      <SkipForward className="w-4 h-4" />
                    </button>
                    <button
                      onClick={onStopPlayback}
                      className="bg-white/10 hover:bg-white/20 transition p-2 rounded-full"
                      title="Stop"
                    >
                      <Square className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Ready to practice?</p>
                    <p className="text-sm text-text-subtle">Play through all {setlist.items.length} songs</p>
                  </div>
                  <button
                    onClick={onStartPlayback}
                    className="bg-brand hover:bg-brand-hover transition px-6 py-2 font-semibold flex items-center gap-2 rounded-pill"
                  >
                    <Play className="w-4 h-4" />
                    Play Setlist
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Continuous mode toggle */}
          {setlist.items.length > 0 && (
            <div className="flex items-center justify-between mb-4 px-3 py-2 bg-white/5 rounded-card-btn">
              <div className="flex items-center gap-2 min-w-0">
                <Car className={`w-4 h-4 flex-shrink-0 ${continuousMode ? 'text-brand' : 'text-text-subtle'}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium">Continuous mode</p>
                  <p className="text-xs text-text-subtle">Skip all gaps — best for listening in the car</p>
                </div>
              </div>
              <button
                onClick={onToggleContinuousMode}
                className={`w-11 h-6 rounded-full transition relative flex-shrink-0 ml-3 ${
                  continuousMode ? 'bg-brand' : 'bg-white/20'
                }`}
                aria-label="Toggle continuous mode"
              >
                <div
                  className="absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow"
                  style={{ left: continuousMode ? '22px' : '2px' }}
                />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={onAddSong}
              className="bg-brand hover:bg-brand-hover transition px-4 py-2 font-semibold text-sm flex items-center gap-2 rounded-pill"
            >
              <Plus className="w-4 h-4" />
              Add Song
            </button>
          </div>

          {/* Songs List */}
          {setlist.items.length === 0 ? (
            <div className="border border-dashed border-surface-border-light rounded-card-btn p-8 text-center">
              <Music className="w-8 h-8 text-text-subtle opacity-50 mx-auto mb-2" />
              <p className="text-text-muted">No songs in this setlist yet</p>
              <p className="text-text-subtle text-sm">Add songs to start building your set</p>
            </div>
          ) : (
            <div
              ref={containerRef}
              className="space-y-2"
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {setlist.items.map((item, idx) => {
                const song = getSongById(item.songId);
                const showInsertBefore = insertIndex === idx;
                const showInsertAfter = insertIndex === idx + 1 && idx === setlist.items.length - 1;
                const isCurrentSong = isCurrentlyPlaying && currentItemIndex === idx;

                return (
                  <div key={item.id}>
                    {showInsertBefore && (
                      <div className="h-1 bg-brand rounded-full mb-2 shadow-lg shadow-brand/50" />
                    )}
                    <div
                      data-item-index={idx}
                      onClick={() => onJumpToSong(idx)}
                      className={`border border-surface-border-light rounded-card-btn p-3 transition cursor-pointer hover:bg-white/5 active:bg-white/10 ${
                        isCurrentSong ? 'ring-2 ring-green-400' : ''
                      } ${draggedIndex === idx ? 'opacity-40' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Drag Handle */}
                        <div
                          onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, idx); }}
                          onTouchStart={(e) => { e.stopPropagation(); handleTouchStart(e, idx); }}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-grab active:cursor-grabbing p-1 text-text-subtle hover:text-text-muted touch-none"
                        >
                          <GripVertical className="w-5 h-5" />
                        </div>

                        {/* Song Number */}
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCurrentSong ? 'bg-green-500' : 'bg-brand'
                        }`}>
                          {isCurrentSong && isPlaying ? (
                            <div className="flex gap-0.5">
                              <div className="w-0.5 h-3 bg-white animate-pulse" />
                              <div className="w-0.5 h-3 bg-white animate-pulse" style={{ animationDelay: '0.1s' }} />
                              <div className="w-0.5 h-3 bg-white animate-pulse" style={{ animationDelay: '0.2s' }} />
                            </div>
                          ) : (
                            <span className="font-bold text-sm">{idx + 1}</span>
                          )}
                        </div>

                        {/* Song Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{song?.name || 'Unknown Song'}</p>
                          <div className="flex items-center gap-2 text-xs text-text-subtle">
                            <span>{formatDuration(item.cachedDuration)}</span>
                            {song?.tempo && <span>• {song.tempo} BPM</span>}
                          </div>
                        </div>

                        {/* Gap Time (hidden when continuous mode active) */}
                        {idx < setlist.items.length - 1 && !continuousMode && (
                          <div className="flex items-center gap-1 text-xs text-text-subtle">
                            <Clock className="w-3 h-3" />
                            <select
                              value={item.gapAfterSeconds || 30}
                              onChange={(e) => onUpdateGapTime(item.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="bg-transparent border border-surface-border-light rounded-card-btn px-1 py-0.5 text-xs text-white"
                            >
                              <option value={0}>0s</option>
                              <option value={10}>10s</option>
                              <option value={20}>20s</option>
                              <option value={30}>30s</option>
                              <option value={45}>45s</option>
                              <option value={60}>60s</option>
                              <option value={90}>90s</option>
                              <option value={120}>2m</option>
                            </select>
                          </div>
                        )}

                        {/* Remove */}
                        <button
                          onClick={(e) => { e.stopPropagation(); onRemoveItem(item.id); }}
                          className="p-1.5 text-text-subtle hover:text-red-400 transition"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    {showInsertAfter && (
                      <div className="h-1 bg-brand rounded-full mt-2 shadow-lg shadow-brand/50" />
                    )}
                  </div>
                );
              })}

              {/* Total Duration */}
              <div className="pt-3 border-t border-surface-border-light flex justify-between text-sm">
                <span className="text-text-subtle">
                  Total Duration {continuousMode && <span className="text-brand">(continuous)</span>}
                </span>
                <span className="font-semibold text-brand">{formatDuration(totalDuration)}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
